import { 
    PublicClientApplication, 
    AccountInfo, 
    SilentRequest,
    InteractionRequiredAuthError
} from "@azure/msal-browser";
import { loginRequest } from "../../config/authConfig";
import { SessionPersistenceService } from "../session";

/**
 * Interface para el estado de autenticación
 */
interface AuthState {
    isAuthenticated: boolean;
    account: AccountInfo | null;
    isLoading: boolean;
    error: string | null;
}

/**
 * Interface para tokens en memoria
 */
interface TokenInfo {
    token: string;
    expiresAt: number;
    scopes: string[];
}

/**
 * Interface para información de ID Token
 */
interface IdTokenInfo {
    token: string;
    expiresAt: number;
}

/**
 * Servicio de autenticación seguro que almacena tokens en memoria
 * y maneja la renovación automática
 */
export class SecureAuthService {
    private msalInstance: PublicClientApplication;
    private accessTokens: Map<string, TokenInfo> = new Map();
    private idTokenInfo: IdTokenInfo | null = null;
    private authState: AuthState = {
        isAuthenticated: false,
        account: null,
        isLoading: false,
        error: null
    };
    private stateListeners: Set<(state: AuthState) => void> = new Set();
    private tokenRefreshBuffer = 5 * 60 * 1000; // 5 minutos antes de expirar

    constructor(msalInstance: PublicClientApplication) {
        this.msalInstance = msalInstance;
        // Inicializar de forma asíncrona
        this.initializeAuthState().catch(error => {
            console.error('Failed to initialize auth state:', error);
        });
    }

    /**
     * Inicializa el estado de autenticación
     */
    private async initializeAuthState(): Promise<void> {
        try {
            // Manejar cualquier resultado de redirección pendiente
            await this.handleRedirectResult();
            
            const accounts = await this.waitForAccountsAvailable();
            if (accounts.length > 0) {
                this.msalInstance.setActiveAccount(accounts[0]);
                
                // Intentar obtener un token válido para verificar que la sesión esté activa
                const token = await this.getValidAccessToken();
                
                this.updateAuthState({
                    isAuthenticated: !!token,
                    account: accounts[0],
                    isLoading: false,
                    error: null
                });
            } else {
                this.updateAuthState({
                    isAuthenticated: false,
                    account: null,
                    isLoading: false,
                    error: null
                });
            }
        } catch (error) {
            console.error('Error initializing auth state:', error);
            this.updateAuthState({
                isAuthenticated: false,
                account: null,
                isLoading: false,
                error: 'Failed to initialize authentication'
            });
        }
    }

    /**
     * Actualiza el estado de autenticación y notifica a los listeners
     */
    private updateAuthState(newState: Partial<AuthState>): void {
        this.authState = { ...this.authState, ...newState };
        this.stateListeners.forEach(listener => listener(this.authState));
    }

    /**
     * Suscribe un listener para cambios en el estado de autenticación
     */
    public onAuthStateChanged(listener: (state: AuthState) => void): () => void {
        this.stateListeners.add(listener);
        // Retorna función para desuscribirse
        return () => this.stateListeners.delete(listener);
    }

    /**
     * Obtiene el estado actual de autenticación
     */
    public getAuthState(): AuthState {
        return { ...this.authState };
    }

    /**
     * Inicializa el servicio de autenticación de forma asíncrona
     * Útil para llamar después de crear la instancia
     */
    public async initialize(): Promise<void> {
        await this.initializeAuthState();
    }

    /**
     * Genera una clave única para el token basada en los scopes
     */
    private getTokenKey(scopes: string[]): string {
        return scopes.sort().join('|');
    }

        /**
     * Espera a que MSAL tenga cuentas disponibles, con timeout máximo
     * Esto resuelve el race condition donde getAllAccounts() retorna vacío
     * mientras MSAL está leyendo el cache de localStorage (en nuevas pestañas)
     * 
     * @param maxTimeout Tiempo máximo de espera en ms (default: 2000)
     * @param retryDelay Delay entre intentos en ms (default: 100)
     * @returns Promise<AccountInfo[]> Array de cuentas disponibles, o array vacío si timeout
     */
    private async waitForAccountsAvailable(
        maxTimeout: number = 2000,
        retryDelay: number = 100
    ): Promise<AccountInfo[]> {
        const startTime = Date.now();
        // Esperar un poco antes de empezar para dar tiempo a MSAL de inicializar
        await new Promise(resolve => setTimeout(resolve, 50));
        
        while (Date.now() - startTime < maxTimeout) {
            const accounts = this.msalInstance.getAllAccounts();
            
            if (accounts.length > 0) {
                if (Date.now() - startTime > 100) {
                    console.log(`waitForAccountsAvailable: Accounts found after ${Date.now() - startTime}ms`);
                }
                return accounts;
            }
            
            // Esperar antes del siguiente intento
            await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
        
        console.log(`waitForAccountsAvailable: No accounts found after ${maxTimeout}ms (this is normal if no session exists)`);
        return [];
    }

    /**
     * Verifica si un token está válido y no ha expirado
     */
    private isTokenValid(tokenInfo: TokenInfo): boolean {
        return Date.now() < (tokenInfo.expiresAt - this.tokenRefreshBuffer);
    }

    /**
     * Verifica si un ID token está válido y no ha expirado
     */
    private isIdTokenValid(idTokenInfo: IdTokenInfo): boolean {
        return Date.now() < (idTokenInfo.expiresAt - this.tokenRefreshBuffer);
    }

    /**
     * Almacena un token en memoria de forma segura
     */
    private storeTokenInMemory(token: string, expiresIn: number, scopes: string[]): void {
        const tokenKey = this.getTokenKey(scopes);
        const expiresAt = Date.now() + (expiresIn * 1000);
        
        this.accessTokens.set(tokenKey, {
            token,
            expiresAt,
            scopes
        });

        // Programar limpieza automática del token
        setTimeout(() => {
            this.accessTokens.delete(tokenKey);
        }, expiresIn * 1000);
    }

    /**
     * Almacena un ID token en memoria de forma segura
     */
    private storeIdTokenInMemory(token: string, expiresIn: number): void {
        const expiresAt = Date.now() + (expiresIn * 1000);
        
        this.idTokenInfo = {
            token,
            expiresAt
        };

        // Programar limpieza automática del token
        setTimeout(() => {
            this.idTokenInfo = null;
        }, expiresIn * 1000);
    }

    /**
     * Obtiene un token válido, renovándolo si es necesario
     */
    public async getValidAccessToken(scopes: string[] = loginRequest.scopes || []): Promise<string | null> {
        try {
            const tokenKey = this.getTokenKey(scopes);
            const cachedToken = this.accessTokens.get(tokenKey);

            // Si tenemos un token válido en memoria, lo retornamos
            if (cachedToken && this.isTokenValid(cachedToken)) {
                return cachedToken.token;
            }

            // Si no hay token válido, intentamos obtener uno nuevo
            return await this.acquireNewToken(scopes);
        } catch (error) {
            console.error('Error getting valid access token:', error);
            this.updateAuthState({ error: 'Failed to get access token' });
            return null;
        }
    }

    /**
     * Obtiene un ID token válido, renovándolo si es necesario
     */
    public async getValidIdToken(scopes: string[] = loginRequest.scopes || []): Promise<string | null> {
        try {
            // Si tenemos un ID token válido en memoria, lo retornamos
            if (this.idTokenInfo && this.isIdTokenValid(this.idTokenInfo)) {
                return this.idTokenInfo.token;
            }

            // Si no hay token válido, intentamos obtener uno nuevo
            return await this.acquireNewIdToken(scopes);
        } catch (error) {
            console.error('Error getting valid ID token:', error);
            this.updateAuthState({ error: 'Failed to get ID token' });
            return null;
        }
    }

    /**
     * Adquiere un nuevo token de forma silenciosa
     */
    private async acquireNewToken(scopes: string[]): Promise<string | null> {
        const account = this.msalInstance.getActiveAccount();
        if (!account) {
            this.updateAuthState({ 
                isAuthenticated: false, 
                error: 'No active account found' 
            });
            return null;
        }

        try {
            const silentRequest: SilentRequest = {
                scopes,
                account,
                forceRefresh: false
            };

            const response = await this.msalInstance.acquireTokenSilent(silentRequest);
            
            if (response.accessToken) {
                // Almacenar token en memoria
                this.storeTokenInMemory(
                    response.accessToken,
                    response.expiresOn ? 
                        Math.floor((response.expiresOn.getTime() - Date.now()) / 1000) : 
                        3600, // Default 1 hora
                    scopes
                );
                
                this.updateAuthState({ error: null });
                return response.accessToken;
            }
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // Token expirado, necesita interacción del usuario
                console.warn('Token expired, user interaction required');
                this.updateAuthState({ 
                    error: 'Token expired, please sign in again' 
                });
                await this.logout();
            } else {
                console.error('Silent token acquisition failed:', error);
                this.updateAuthState({ 
                    error: 'Failed to refresh token' 
                });
            }
        }

        return null;
    }

    /**
     * Adquiere un nuevo ID token de forma silenciosa
     */
    private async acquireNewIdToken(scopes: string[]): Promise<string | null> {
        const account = this.msalInstance.getActiveAccount();
        if (!account) {
            this.updateAuthState({ 
                isAuthenticated: false, 
                error: 'No active account found' 
            });
            return null;
        }

        try {
            const silentRequest: SilentRequest = {
                scopes,
                account,
                forceRefresh: false
            };

            const response = await this.msalInstance.acquireTokenSilent(silentRequest);
            
            if (response.idToken) {
                // Almacenar ID token en memoria
                this.storeIdTokenInMemory(
                    response.idToken,
                    response.expiresOn ? 
                        Math.floor((response.expiresOn.getTime() - Date.now()) / 1000) : 
                        3600 // Default 1 hora
                );
                
                this.updateAuthState({ error: null });
                return response.idToken;
            }
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // Token expirado, necesita interacción del usuario
                console.warn('ID Token expired, user interaction required');
                this.updateAuthState({ 
                    error: 'ID Token expired, please sign in again' 
                });
                await this.logout();
            } else {
                console.error('Silent ID token acquisition failed:', error);
                this.updateAuthState({ 
                    error: 'Failed to refresh ID token' 
                });
            }
        }

        return null;
    }

    /**
     * Inicia sesión con redirección
     */
    public async loginRedirect(): Promise<void> {
        try {
            this.updateAuthState({ isLoading: true, error: null });
            await this.msalInstance.loginRedirect(loginRequest);
        } catch (error) {
            console.error('Login redirect failed:', error);
            this.updateAuthState({ 
                isLoading: false, 
                error: 'Login failed' 
            });
        }
    }

    /**
     * Inicia sesión con popup
     */
    public async loginPopup(): Promise<void> {
        try {
            this.updateAuthState({ isLoading: true, error: null });
            const response = await this.msalInstance.loginPopup(loginRequest);
            
            if (response.account) {
                this.msalInstance.setActiveAccount(response.account);
                
                // Almacenar ID token si está disponible
                if (response.idToken) {
                    this.storeIdTokenInMemory(
                        response.idToken,
                        response.expiresOn ? 
                            Math.floor((response.expiresOn.getTime() - Date.now()) / 1000) : 
                            3600
                    );
                }
                
                this.updateAuthState({
                    isAuthenticated: true,
                    account: response.account,
                    isLoading: false,
                    error: null
                });

                // Guardar metadatos de sesión
                SessionPersistenceService.saveSessionMetadata(response.account);
            }
        } catch (error) {
            console.error('Login popup failed:', error);
            this.updateAuthState({ 
                isLoading: false, 
                error: 'Login failed' 
            });
        }
    }

    /**
     * Maneja el resultado de la redirección de login
     */
    public async handleRedirectResult(): Promise<void> {
        try {
            const response = await this.msalInstance.handleRedirectPromise();
            
            if (response && response.account) {
                this.msalInstance.setActiveAccount(response.account);
                
                // Almacenar ID token si está disponible
                if (response.idToken) {
                    this.storeIdTokenInMemory(
                        response.idToken,
                        response.expiresOn ? 
                            Math.floor((response.expiresOn.getTime() - Date.now()) / 1000) : 
                            3600
                    );
                }
                
                this.updateAuthState({
                    isAuthenticated: true,
                    account: response.account,
                    isLoading: false,
                    error: null
                });

                // Guardar metadatos de sesión
                SessionPersistenceService.saveSessionMetadata(response.account);
            }
        } catch (error) {
            console.error('Handle redirect failed:', error);
            this.updateAuthState({ 
                isLoading: false, 
                error: 'Authentication failed' 
            });
        }
    }

    /**
     * Cierra sesión y limpia todos los tokens
     */
    public async logout(): Promise<void> {
        try {
            // Limpiar tokens de memoria
            this.accessTokens.clear();
            this.idTokenInfo = null;
            
            // Limpiar metadatos de sesión
            SessionPersistenceService.clearSessionMetadata();
            
            // Actualizar estado
            this.updateAuthState({
                isAuthenticated: false,
                account: null,
                isLoading: false,
                error: null
            });

            // Logout de MSAL
            await this.msalInstance.logoutRedirect({
                postLogoutRedirectUri: window.location.origin
            });
        } catch (error) {
            console.error('Logout failed:', error);
            // Aún así limpiar el estado local
            this.accessTokens.clear();
            this.idTokenInfo = null;
            this.updateAuthState({
                isAuthenticated: false,
                account: null,
                isLoading: false,
                error: 'Logout failed'
            });
        }
    }

    /**
     * Fuerza la renovación de un token específico
     */
    public async forceTokenRefresh(scopes: string[] = loginRequest.scopes || []): Promise<string | null> {
        const account = this.msalInstance.getActiveAccount();
        if (!account) return null;

        try {
            const silentRequest: SilentRequest = {
                scopes,
                account,
                forceRefresh: true // Forzar renovación
            };

            const response = await this.msalInstance.acquireTokenSilent(silentRequest);
            
            if (response.accessToken) {
                this.storeTokenInMemory(
                    response.accessToken,
                    response.expiresOn ? 
                        Math.floor((response.expiresOn.getTime() - Date.now()) / 1000) : 
                        3600,
                    scopes
                );
                return response.accessToken;
            }
        } catch (error) {
            console.error('Force token refresh failed:', error);
        }

        return null;
    }

    /**
     * Fuerza la renovación de un ID token específico
     */
    public async forceIdTokenRefresh(scopes: string[] = loginRequest.scopes || []): Promise<string | null> {
        const account = this.msalInstance.getActiveAccount();
        if (!account) return null;

        try {
            const silentRequest: SilentRequest = {
                scopes,
                account,
                forceRefresh: true // Forzar renovación
            };

            const response = await this.msalInstance.acquireTokenSilent(silentRequest);
            
            if (response.idToken) {
                this.storeIdTokenInMemory(
                    response.idToken,
                    response.expiresOn ? 
                        Math.floor((response.expiresOn.getTime() - Date.now()) / 1000) : 
                        3600
                );
                return response.idToken;
            }
        } catch (error) {
            console.error('Force ID token refresh failed:', error);
        }

        return null;
    }

    /**
     * Limpia todos los tokens de memoria (útil para testing o logout parcial)
     */
    public clearTokenCache(): void {
        this.accessTokens.clear();
        this.idTokenInfo = null;
    }

    /**
     * Obtiene información sobre los tokens almacenados (para debugging)
     */
    public getTokenCacheInfo(): {
        accessTokens: Array<{scopes: string[], expiresAt: Date, isValid: boolean}>,
        idToken: {expiresAt: Date, isValid: boolean} | null
    } {
        const accessTokens: Array<{scopes: string[], expiresAt: Date, isValid: boolean}> = [];
        
        this.accessTokens.forEach((tokenInfo) => {
            accessTokens.push({
                scopes: tokenInfo.scopes,
                expiresAt: new Date(tokenInfo.expiresAt),
                isValid: this.isTokenValid(tokenInfo)
            });
        });

        const idToken = this.idTokenInfo ? {
            expiresAt: new Date(this.idTokenInfo.expiresAt),
            isValid: this.isIdTokenValid(this.idTokenInfo)
        } : null;
        
        return { accessTokens, idToken };
    }
}